import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import {
  Wheel,
  ConfigValue,
  injectCSSVariablesIntoDOM,
  FontTypes,
  emitIframeMessage,
  IframeMessage,
} from '@longnecktech/splash-commons-fe';
import { SessionService } from '@services/session.service';
import { tap, Observable, map, BehaviorSubject, finalize, of, filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  private http = inject(HttpClient);
  private session = inject(SessionService);

  private _isLoading = new BehaviorSubject<boolean>(false);
  private _latestGame = new BehaviorSubject<Wheel | undefined>(undefined);

  readonly isLoading$ = this._isLoading.asObservable();
  readonly latestGame$ = this._latestGame.asObservable();

  fetchGame(): Observable<Wheel> {
    this._isLoading.next(true);
    return this.http
      .get<Wheel>(
        `${environment.backendUrl}/api/wheel/game?${this.getQueryParams()}`,
      )
      .pipe(
        filter((game): game is Wheel => !!game),
        map((game) => ({
          ...game,
          segments: (game.segments || [])
            .slice()
            .sort((a, b) => a.index - b.index),
        })),
        tap((data: Wheel) => {
          this.session.setCurrentGame(data);
          this.session.updateAuthToken();
          this.setCssVariables(data);
          this.removeGlobalLoader();
        }),
        finalize(() => {
          this.removeGlobalLoader();
          this._isLoading.next(false);
          emitIframeMessage(IframeMessage.GameLoaded);
        }),
      );
  }

  fetchLatestGame(): Observable<Wheel> {
    this._isLoading.next(true);
    const currentGame = this.session.getCurrentGame()!;
    // if game is ended or paused or user is ineligible and game contains featured products,
    // return the game immediately
    if (
      (currentGame.ended ||
        currentGame.paused ||
        currentGame.segments.length === 0) &&
      currentGame.featuredProducts?.length
    ) {
      this._latestGame.next(currentGame);
      return of(currentGame);
    }
    // otherwise, fetch the latest finished game
    const instance = currentGame.instance;
    const params = instance ? `instance=${instance}` : '';
    return this.http
      .get<Wheel>(`${environment.backendUrl}/api/wheel/game?${params}`)
      .pipe(
        filter((game): game is Wheel => !!game),
        tap((game: Wheel) => {
          this._latestGame.next(game);
        }),
        finalize(() => {
          this._isLoading.next(false);
        }),
      );
  }

  private setCssVariables(game: Wheel): void {
    if (!game.theme) return;
    const styleEl = document.createElement('style');
    styleEl.textContent = injectCSSVariablesIntoDOM(game.theme, {
      defaultSecondaryCtaButtonColors: {
        '--z-cta-buttons-outline-background-color': 1,
        '--color-accent-1': 2,
      },
      defaultFonts: {
        [FontTypes.Regular]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Regular.ttf',
        [FontTypes.Bold]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Bold.ttf',
        [FontTypes.Medium]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Medium.ttf',
        [FontTypes.Headlines]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Regular.ttf',
      },
      defaultCtaBorderRadius: 28,
      customMaterialVariables: this.getOverwrittenAngularMaterialsVariables(
        game.theme.cssVariables,
      ),
    });
    document.head.appendChild(styleEl);
  }

  private getOverwrittenAngularMaterialsVariables(
    themeVariables: ConfigValue[],
  ): string {
    const baseColor = themeVariables.find((v) => v.key === '--color-base-1');
    const base25Color = themeVariables.find((v) => v.key === '--color-base-2');
    const textColor = themeVariables.find(
      (v) => v.key === '--color-neutral-100',
    );

    return `
    --mat-expansion-container-background-color: ${baseColor?.value};
    --mat-expansion-container-text-color: ${textColor?.value};
    --mat-dialog-container-shape: 0;
    --mat-dialog-container-color: ${base25Color?.value};
    `;
  }

  private getQueryParams(): string {
    const params: { instance?: string; gameUuid?: string } = {};
    if (this.session.instance) params['instance'] = this.session.instance;
    if (this.session.gameUuid) params['gameUuid'] = this.session.gameUuid;
    return new URLSearchParams(params).toString();
  }

  private removeGlobalLoader(): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('global-loader');
    if (el) {
      el.remove();
    }
  }
}
